import { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import LoadingWithBackdrop from '~/components/loadingWithBackdrop'
import NoAgentAccountPortalMessage from '~/components/noAgentAccountPortalMessage'
import NotFound from '~/components/NotFound'
import useGetAgentCentralized from '~/features/agents/hooks/useGetAgentCentralized'
import { EAgentKeys } from '~/features/agents/models'
import useUpdateOrganizationStore from '~/features/organizations/hooks/useUpdateOrganizationStore'
import PartnerThemeWrapper from '~/features/partners/components/partnerThemeWrapper'
import usePrivateRoutesRedirect from '~/hooks/usePrivateRoutesRedirect'
import useRedirectOnEmptyStore from '~/hooks/useRedirectOnEmptyStore'
import ContentWrapper from '~/layouts/account/contentWrapper'
import ScenariosLayout from '~/layouts/account/scenariosLayout'
import SettingsLayout from '~/layouts/account/settingsLayout'
import { EClientPath } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import { sessionStore } from '~/store'

function AccountLayout() {
  useRedirectOnEmptyStore()
  const { isLoading: isLoadingGettingAgents, selectedAgent } =
    useGetAgentCentralized()
  const isLoadingOrganization = useUpdateOrganizationStore()
  const isAuthCheckComplete = usePrivateRoutesRedirect({
    requiredUserGroups: null,
  })
  const { pathname } = useLocation()
  const isSettingsPage = pathname.includes(EClientPath.settingsBase)
  const isScenariosPage =
    pathname.includes(EClientPath.scenariosBase) &&
    import.meta.env[EEnvironmentVariables.VITE_IS_SCENARIOS_ENABLED] === 'true'
  const isInboxPage = pathname.includes(EClientPath.inbox)

  const [agentId, isLoadingUser] = sessionStore((state) => [
    state.agent.id,
    state.UIElements.isLoadingUser,
  ])

  const isLoading = isLoadingOrganization || isLoadingGettingAgents

  if (!isAuthCheckComplete) {
    return <LoadingWithBackdrop show />
  }

  const isWrongAgent =
    !!agentId && !isLoading && !selectedAgent?.[EAgentKeys.id]

  return (
    <Suspense fallback={<LoadingWithBackdrop show />}>
      <PartnerThemeWrapper>
        <ContentWrapper
          isLoading={
            (!isSettingsPage && isLoading) ||
            isLoadingGettingAgents ||
            isLoadingUser
          }
          noPadding={isInboxPage}
        >
          {isWrongAgent && <NotFound message="Assistant not found" />}
          {/* Don't show loading indicator on settings page as it has its own loading indicator. */}
          {!isWrongAgent && !agentId && !isLoading && (
            <NoAgentAccountPortalMessage />
          )}
          {!isWrongAgent && !!agentId && isSettingsPage && (
            <SettingsLayout>
              <Outlet />
            </SettingsLayout>
          )}
          {!isWrongAgent && !!agentId && isScenariosPage && (
            <ScenariosLayout>
              <Outlet />
            </ScenariosLayout>
          )}
          {!isWrongAgent &&
            !!agentId &&
            !isSettingsPage &&
            !isScenariosPage && <Outlet />}
        </ContentWrapper>
      </PartnerThemeWrapper>
    </Suspense>
  )
}

export default AccountLayout
